module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<p>This is an introduction to core five classes required to constructing a representation from the controller, this case the root of the API because it is the simplest. This pattern is the mainstay that variation emanate from and the files <em>tend</em> to live across two projects. The tutorial provides various views:</p>\n<ul>\n<li>IDE/code structure of classes</li>\n<li>Block diagram</li>\n<li>Sequence diagram</li>\n<li>Actual code</li>\n<li>curl request/response</li>\n</ul>\n<p><img src=\"/backend/aspnetcore-todo-api/home-api.png\" alt=\"image of the localhost:5000\"></p>\n<h3>Goal</h3>\n<ul>\n<li>Return an unauthenticated resource on the root/home of the API (simple GET)</li>\n<li>Learn the code layering of the solution</li>\n<li>Learn about creating link relations and their URIs</li>\n</ul>\n<blockquote>\n<p>Note: this code assumes that the MVC pipeline is already setup</p>\n</blockquote>\n<h3>Anatomy of code</h3>\n<p>Below, you see on overview of the code, then you see the implementation, finally we’ll draw it again in a way to help\nyou start to remember it! Remember, this is essentially the pattern of code you’ll use all the time with adding representations to the domain. No more, no less.</p>\n<Instruction>\n<p>Read and start to grok the layout of the underlying code base</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\"...todo-hypermedia/api\"  nocopy=\"false\"><code><span class=\"token keyword\">.</span>\n├── Api\n│   ├── Controllers\n│   │   └── HomeController.cs\n│   ├── RepresentationExtensions\n│   │   └── ApiRepresentationExtensions.cs\n│   └── UriFactory\n│       └── HomeUriFactory.cs\n└── Domain\n    ├── Models\n    │   └── ApiVersion.cs\n    └── Representation\n        └── ApiRepresentation.cs\n</code></pre>\n      </div>\n</Instruction>\n<h4>Logical Resource through a controller action</h4>\n<p>For the more visual, below is a diagram that layers between the Representation and the Model where:</p>\n<ul>\n<li><code>Representation</code>s are serialised in and out across the request and response, respectively</li>\n<li><code>FromRepresentation</code> is an incoming representation  mapped to a domain model (that may be validated and persisted, ie apply business process/rules)</li>\n<li><code>ToRepresentation</code> is an outgoing representation constructed from a domain with link relations created through a <code>UriFactory</code> that maps resource identities to routes (ie back into the controller actions)</li>\n<li><code>Model</code> itself are the in-memory domain objects from persistence</li>\n</ul>\n<blockquote>\n<p>Note: each of the boundaries denoted by box lines indicate potential impedance mismatch that should be understandable in the code</p>\n<p>This diagram is generalisable to most MVC frameworks and suggest that perhaps we might need to find variations of the pattern to meet hypermedia designed APIs</p>\n</blockquote>\n<p><img src=\"/backend/aspnetcore-todo-api/logical-resource.png\" alt=\"logical resource \"></p>\n<h4>GET: sequencing request to response</h4>\n<p>Below is a way to understand the interaction of the pattern of representation request/response:</p>\n<p><img src=\"/backend/aspnetcore-todo-api/get-request-response.png\" alt=\"Get request/response\"></p>\n<h3>The code</h3>\n<h3>Domain</h3>\n<Instruction>\n<p>Create a domain model for the home api that has the version of the application.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-csharp\"  path=\"...todo-hypermedia/api/Domain/Models/ApiVersion.cs\"  nocopy=\"false\"><code><span class=\"token keyword\">namespace</span> Domain<span class=\"token punctuation\">.</span>Models\n<span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">public</span> <span class=\"token keyword\">class</span> <span class=\"token class-name\">ApiVersion</span>\n    <span class=\"token punctuation\">{</span>\n        <span class=\"token keyword\">public</span> <span class=\"token keyword\">string</span> Version <span class=\"token punctuation\">{</span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">;</span> <span class=\"token keyword\">set</span><span class=\"token punctuation\">;</span> <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Create a representation for the home api including the serialisation information.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-csharp\"  path=\"...todo-hypermedia/api/Domain/Representation/ApiRepresentation.cs\"  nocopy=\"false\"><code><span class=\"token keyword\">using</span> System<span class=\"token punctuation\">.</span>Runtime<span class=\"token punctuation\">.</span>Serialization<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> SemanticLink<span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">namespace</span> Domain<span class=\"token punctuation\">.</span>Representation\n<span class=\"token punctuation\">{</span>\n    <span class=\"token comment\" spellcheck=\"true\">/// &lt;summary></span>\n    <span class=\"token comment\" spellcheck=\"true\">///     The API is a link of links to resources</span>\n    <span class=\"token comment\" spellcheck=\"true\">/// &lt;/summary></span>\n    <span class=\"token punctuation\">[</span><span class=\"token function\">DataContract</span><span class=\"token punctuation\">(</span>Name <span class=\"token operator\">=</span> <span class=\"token string\">\"api\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">]</span>\n    <span class=\"token keyword\">public</span> <span class=\"token keyword\">class</span> <span class=\"token class-name\">ApiRepresentation</span> <span class=\"token punctuation\">:</span> LinkedRepresentation\n    <span class=\"token punctuation\">{</span>\n        <span class=\"token punctuation\">[</span><span class=\"token function\">DataMember</span><span class=\"token punctuation\">(</span>Name <span class=\"token operator\">=</span> <span class=\"token string\">\"version\"</span><span class=\"token punctuation\">,</span> Order <span class=\"token operator\">=</span> <span class=\"token number\">20</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">]</span>\n        <span class=\"token keyword\">public</span> <span class=\"token keyword\">string</span> Version <span class=\"token punctuation\">{</span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">;</span> <span class=\"token keyword\">set</span><span class=\"token punctuation\">;</span> <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Controller</h3>\n<Instruction>\n<p>Create a controller (with version injected) and an method that responds to the root URI.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-csharp\"  path=\"...todo-hypermedia/api/Api/Controllers/HomeController.cs\"  nocopy=\"false\"><code><span class=\"token keyword\">using</span> System<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Api<span class=\"token punctuation\">.</span>RepresentationExtensions<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Api<span class=\"token punctuation\">.</span>UriFactory<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Domain<span class=\"token punctuation\">.</span>Models<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Domain<span class=\"token punctuation\">.</span>Representation<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Marvin<span class=\"token punctuation\">.</span>Cache<span class=\"token punctuation\">.</span>Headers<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Microsoft<span class=\"token punctuation\">.</span>AspNetCore<span class=\"token punctuation\">.</span>Mvc<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> SemanticLink<span class=\"token punctuation\">.</span>AspNetCore<span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">namespace</span> Api<span class=\"token punctuation\">.</span>Controllers\n <span class=\"token punctuation\">{</span>\n     <span class=\"token punctuation\">[</span><span class=\"token function\">Route</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">]</span>\n     <span class=\"token keyword\">public</span> <span class=\"token keyword\">class</span> <span class=\"token class-name\">HomeController</span> <span class=\"token punctuation\">:</span> Controller\n     <span class=\"token punctuation\">{</span>\n         <span class=\"token keyword\">private</span> <span class=\"token keyword\">readonly</span> Version _version<span class=\"token punctuation\">;</span>\n\n         <span class=\"token keyword\">public</span> <span class=\"token function\">HomeController</span><span class=\"token punctuation\">(</span>Version version<span class=\"token punctuation\">)</span>\n         <span class=\"token punctuation\">{</span>\n             <span class=\"token comment\" spellcheck=\"true\">// version is from the DLL version and is injected</span>\n             _version <span class=\"token operator\">=</span> version<span class=\"token punctuation\">;</span>\n         <span class=\"token punctuation\">}</span>\n\n         <span class=\"token comment\" spellcheck=\"true\">/// &lt;summary></span>\n         <span class=\"token comment\" spellcheck=\"true\">///     The root/home of the API</span>\n         <span class=\"token comment\" spellcheck=\"true\">/// &lt;/summary></span>\n         <span class=\"token comment\" spellcheck=\"true\">/// &lt;remarks></span>\n         <span class=\"token comment\" spellcheck=\"true\">///    This is always unauthenticated and must not disclose information and **must** also be general enough to</span>\n         <span class=\"token comment\" spellcheck=\"true\">///     everyone so that it can be publicly cacheable.</span>\n         <span class=\"token comment\" spellcheck=\"true\">/// &lt;/remarks></span>\n<span class=\"gatsby-highlight-code-line\">         <span class=\"token punctuation\">[</span><span class=\"token function\">HttpGet</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"\"</span><span class=\"token punctuation\">,</span> Name <span class=\"token operator\">=</span> HomeUriFactory<span class=\"token punctuation\">.</span>DefaultRoute<span class=\"token punctuation\">)</span><span class=\"token punctuation\">]</span>\n</span>         <span class=\"token punctuation\">[</span><span class=\"token function\">HttpCacheExpiration</span><span class=\"token punctuation\">(</span>CacheLocation <span class=\"token operator\">=</span> CacheLocation<span class=\"token punctuation\">.</span>Public<span class=\"token punctuation\">,</span> MaxAge <span class=\"token operator\">=</span> CacheDuration<span class=\"token punctuation\">.</span>Long<span class=\"token punctuation\">)</span><span class=\"token punctuation\">]</span>\n         <span class=\"token keyword\">public</span> ApiRepresentation <span class=\"token function\">GetApi</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span>\n         <span class=\"token punctuation\">{</span>\n             <span class=\"token keyword\">return</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">ApiVersion</span>\n                 <span class=\"token punctuation\">{</span>\n                     Version <span class=\"token operator\">=</span> _version<span class=\"token punctuation\">.</span><span class=\"token function\">ToString</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span>\n                 <span class=\"token punctuation\">}</span>\n<span class=\"gatsby-highlight-code-line\">                 <span class=\"token punctuation\">.</span><span class=\"token function\">ToRepresentation</span><span class=\"token punctuation\">(</span>Url<span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span>         <span class=\"token punctuation\">}</span>\n\n         <span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span>\n\n <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Factories/Helpers</h3>\n<Instruction>\n<p>Create a <code>ToRepresentation</code> implementation as an extension method that constructs the <code>ApiRepresentation</code> based on the incoming <code>ApiVersion</code> model and creates a mandatory ‘self’ link relation with the <code>UriFactory</code>.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-csharp\"  path=\"...todo-hypermedia/api/Api/RepresentationExtensions/ApiRepresentationExtensions.cs\"  nocopy=\"false\"><code><span class=\"token keyword\">using</span> Api<span class=\"token punctuation\">.</span>UriFactory<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Domain<span class=\"token punctuation\">.</span>LinkRelations<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Domain<span class=\"token punctuation\">.</span>Models<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Domain<span class=\"token punctuation\">.</span>Representation<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> Microsoft<span class=\"token punctuation\">.</span>AspNetCore<span class=\"token punctuation\">.</span>Mvc<span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">using</span> SemanticLink<span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">namespace</span> Api<span class=\"token punctuation\">.</span>RepresentationExtensions\n<span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">public</span> <span class=\"token keyword\">static</span> <span class=\"token keyword\">class</span> <span class=\"token class-name\">ApiRepresentationExtensions</span>\n    <span class=\"token punctuation\">{</span>\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;summary></span>\n        <span class=\"token comment\" spellcheck=\"true\">///     The root/home API</span>\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;/summary></span>\n        <span class=\"token keyword\">public</span> <span class=\"token keyword\">static</span> ApiRepresentation <span class=\"token function\">ToRepresentation</span><span class=\"token punctuation\">(</span><span class=\"token keyword\">this</span> ApiVersion api<span class=\"token punctuation\">,</span> IUrlHelper url<span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">{</span>\n            <span class=\"token keyword\">return</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">ApiRepresentation</span>\n            <span class=\"token punctuation\">{</span>\n                Links <span class=\"token operator\">=</span> <span class=\"token keyword\">new</span><span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span>\n                <span class=\"token punctuation\">{</span>\n                    <span class=\"token comment\" spellcheck=\"true\">// root of the api</span>\n                    url<span class=\"token punctuation\">.</span><span class=\"token function\">MakeHomeUri</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span><span class=\"token function\">MakeWebLink</span><span class=\"token punctuation\">(</span>IanaLinkRelation<span class=\"token punctuation\">.</span>Self<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n\n                    <span class=\"token comment\" spellcheck=\"true\">// the actual resource has more</span>\n                    <span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span><span class=\"token punctuation\">.</span>\n                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n                Version <span class=\"token operator\">=</span> api<span class=\"token punctuation\">.</span>Version\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n        <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Create a <code>UriFactory</code> implementation as an extension method which creates a URI string based on the route as configured up in the controller. Note: the URL construction is part of the MVC framework itself and thus needs to be handed through.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-csharp\"  path=\"...todo-hypermedia/api/Api/UriFactory/HomeUriFactory.cs\"  nocopy=\"false\"><code><span class=\"token keyword\">using</span> Microsoft<span class=\"token punctuation\">.</span>AspNetCore<span class=\"token punctuation\">.</span>Mvc<span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">namespace</span> Api<span class=\"token punctuation\">.</span>UriFactory\n<span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">public</span> <span class=\"token keyword\">static</span> <span class=\"token keyword\">class</span> <span class=\"token class-name\">HomeUriFactory</span>\n    <span class=\"token punctuation\">{</span>\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;summary></span>\n        <span class=\"token comment\" spellcheck=\"true\">///     The route name for home/root of the API</span>\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;/summary></span>\n        <span class=\"token keyword\">public</span> <span class=\"token keyword\">const</span> <span class=\"token keyword\">string</span> DefaultRoute <span class=\"token operator\">=</span> <span class=\"token string\">\"Home\"</span><span class=\"token punctuation\">;</span>\n\n\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;summary></span>\n        <span class=\"token comment\" spellcheck=\"true\">///     The url of the home/root of the API</span>\n        <span class=\"token comment\" spellcheck=\"true\">/// &lt;/summary></span>\n        <span class=\"token keyword\">public</span> <span class=\"token keyword\">static</span> <span class=\"token keyword\">string</span> <span class=\"token function\">MakeHomeUri</span><span class=\"token punctuation\">(</span><span class=\"token keyword\">this</span> IUrlHelper url<span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">{</span>\n            <span class=\"token keyword\">return</span> url<span class=\"token punctuation\">.</span><span class=\"token function\">Link</span><span class=\"token punctuation\">(</span>DefaultRoute<span class=\"token punctuation\">,</span> <span class=\"token keyword\">new</span> <span class=\"token punctuation\">{</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        <span class=\"token punctuation\">}</span>\n\n    <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Request in curl</h3>\n<blockquote>\n<p>Note: if running the todo API itself, more links will be returned.</p>\n</blockquote>\n<Instruction>\n<p>Now the API should respond to an unauthenticated root request</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"   nocopy=\"false\"><code>$ curl localhost:5000 -v\n\n* Rebuilt URL to: localhost:5000/\n*   Trying 127.0.0.1<span class=\"token punctuation\">..</span>.\n* TCP_NODELAY <span class=\"token keyword\">set</span>\n* Connected to localhost <span class=\"token punctuation\">(</span>127.0.0.1<span class=\"token punctuation\">)</span> port 5000 <span class=\"token punctuation\">(</span><span class=\"token comment\" spellcheck=\"true\">#0)</span>\n<span class=\"token operator\">></span> GET / HTTP/1.1\n<span class=\"token operator\">></span> Host: localhost:5000\n<span class=\"token operator\">></span> User-Agent: curl/7.54.0\n<span class=\"token operator\">></span> Accept: */*\n<span class=\"token operator\">></span>\n<span class=\"token operator\">&lt;</span> HTTP/1.1 200 OK\n<span class=\"token operator\">&lt;</span> Date: Mon, 19 Nov 2018 01:10:33 GMT\n<span class=\"token operator\">&lt;</span> Content-Type: application/json<span class=\"token punctuation\">;</span> charset<span class=\"token operator\">=</span>utf-8\n<span class=\"token operator\">&lt;</span> Server: Kestrel\n<span class=\"token operator\">&lt;</span> Cache-Control: public,max-age<span class=\"token operator\">=</span>3600\n<span class=\"token operator\">&lt;</span> Transfer-Encoding: chunked\n<span class=\"token operator\">&lt;</span> Expires: Mon, 19 Nov 2018 02:10:33 GMT\n<span class=\"token operator\">&lt;</span> Last-Modified: Mon, 19 Nov 2018 01:10:33 GMT\n<span class=\"token operator\">&lt;</span> ETag: <span class=\"token string\">\"7E909A8AC713673962B2CAF1654FB9A7\"</span>\n<span class=\"token operator\">&lt;</span> Vary: Accept, Accept-Language, Accept-Encoding\n<span class=\"token operator\">&lt;</span>\n* Connection <span class=\"token comment\" spellcheck=\"true\">#0 to host localhost left intact</span>\n<span class=\"token punctuation\">{</span> <span class=\"token string\">\"links\"</span>:<span class=\"token punctuation\">[</span> <span class=\"token punctuation\">{</span><span class=\"token string\">\"rel\"</span><span class=\"token keyword\">:</span><span class=\"token string\">\"self\"</span>,<span class=\"token string\">\"href\"</span><span class=\"token keyword\">:</span><span class=\"token string\">\"http://localhost:5000/\"</span><span class=\"token punctuation\">}</span> <span class=\"token punctuation\">]</span>, <span class=\"token string\">\"version\"</span><span class=\"token keyword\">:</span><span class=\"token string\">\"1.0.0.0\"</span><span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>",
			"fields": {
				"slug": "/aspnetcore-todo-api/4-constructing-representations/"
			},
			"excerpt": "This is an introduction to core five classes required to constructing a representation from the controller, this case the root of the API...",
			"frontmatter": {
				"title": "Constructing Representations",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/Users/todd/src/howtohypermedia/content/backend/aspnetcore-todo-api/4-constructing-representations.md absPath of file",
				"question": "How should we version an API?",
				"answers": [
					"We shouldn't as well known authors tell us",
					"In the URI",
					"With a header",
					"As a field in the root as a known place because really we don't need versioning that much"
				],
				"correctAnswer": 3,
				"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version",
				"pageTitle": "Construct the simplest representation - the root/home of the API"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Collection Resource",
							"description": "Learn the code mechanics of a edit forms on a collection resource that uses different mime-types"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/11-update-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Singleton Resource",
							"description": "Learn the code mechanics of a edit forms on a singleton resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/10-update-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a hypermedia API that works within the constraints of REST"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a hypermedia API "
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "",
							"description": null
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/2-modelling-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Modelling Domain",
							"description": "Learn how to diagram the API textually and get also get visual output"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/3-modelling-domain-link-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Representations",
							"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/4-constructing-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Collections",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/6-constructing-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Searchable Resources",
							"description": "Learn the code mechanics of a search forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/8-constructing-search-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Singletons",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/5-constructing-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Create Resource",
							"description": "Learn the code mechanics of a create forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/9-create-resource/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Virtual Resource",
							"description": "Learn the code mechanics of a virtual resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/7-constructing-viritual/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a todo app and admin functions over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side URLs and routing",
							"description": "Design your application around the holding and reconstructing client state through the URI in the browser location requires matching client views and the data it requires"
						},
						"fields": {
							"slug": "/vue-todo-app/2-client-side-urls-and-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting started",
							"description": "Learn how to build a todo client to use the hypermedia API "
						},
						"fields": {
							"slug": "/vue-todo-app/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side application cache",
							"description": "Client need to have one copy of representations in the application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/3-application-cache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Updates and deletes",
							"description": "The application cache also deals with the updates and deletion of resources"
						},
						"fields": {
							"slug": "/vue-todo-app/4-updates-and-deletes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "On-demand Authentication",
							"description": "Be able to create and renew authentication tokens when the server requests valid authentication tokens"
						},
						"fields": {
							"slug": "/vue-todo-app/5-on-demand-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to build a todo app over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/7-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Cloning Resources",
							"description": "Forms in hypermedia make cloning of trees straightforward and we can leverage that with drag and drop functionality alongside the client-side application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/6-cloning-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "HTML representation",
							"description": "Learn what and why to build HTML representation of data with code on demand"
						},
						"fields": {
							"slug": "/vue-api-browser/2-build-html-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Generate HTML",
							"description": "Learn how to respond as HTML in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/3-build-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Register text/html",
							"description": "Learn how to respond to HTML content negotiation in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/4-register-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GET application/json",
							"description": "Learn how to request JSON from the client"
						},
						"fields": {
							"slug": "/vue-api-browser/5-request-json/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to user hypermedia as engine to navigate an API and make changes"
						},
						"fields": {
							"slug": "/vue-api-browser/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying with interaction",
							"description": "Learn how to process JSON to make delete, create and update"
						},
						"fields": {
							"slug": "/vue-api-browser/6-displaying-with-interaction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying forms",
							"description": "Learn how to process form values for create and edit forms"
						},
						"fields": {
							"slug": "/vue-api-browser/8-displaying-form-values/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deleting a resource",
							"description": "Learn how to process JSON to try and delete a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/7-try-delete-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about hypermedia and why a REST-style of architecture is simple and subtle. It's not really for the lazy or those who like to be clever."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Minimally layered design",
							"description": "Learn why hypermedia is a thin layer across persistence that should guide us through the business problem."
						},
						"fields": {
							"slug": "/basics/1-minimally-layered-design/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Submitting forms",
							"description": "Learn how to process creates (POST) and updates (PUT) on a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/9-submitting-forms/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Trends)",
							"description": "Learn that distributed applications have trended towards data and business logic being processed at the client edge"
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Hypermedia for the dedicated",
							"description": "Learn some techniques that will ease the server and client development"
						},
						"fields": {
							"slug": "/basics/4-for-the-dedicated/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigating a hypermedia API",
							"description": "Learn what a hypermedia looks like to be able to explore a domain."
						},
						"fields": {
							"slug": "/basics/2-navigating-api/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Multiple clients",
							"description": "Learn that most APIs should have multiple types of clients."
						},
						"fields": {
							"slug": "/advanced/0-multiple-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "API Modelling",
							"description": "Learn about how collections and singletons are modelled in hypermedia"
						},
						"fields": {
							"slug": "/advanced/1-api-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Forms Modelling",
							"description": "Links and Forms are used to update resources and controlled by the server that the client then uses"
						},
						"fields": {
							"slug": "/advanced/2-forms-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common questions about these tutorials"
						},
						"fields": {
							"slug": "/advanced/4-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Todo Domain",
							"description": "Learn about domain of the API"
						},
						"fields": {
							"slug": "/advanced/3-todo-domain/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/aspnetcore-todo-api/4-constructing-representations/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/aspnetcore-todo-api-4-constructing-representations.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/aspnetcore-todo-api-4-constructing-representations.json
// module chunks = 35